/* ═══════════════════════════════════════════════════════════════
   NOVA PROOF HARNESS — v1.0.0
   Runtime acceptance test execution + evidence storage
   
   GATING:
   - localStorage.nova_proof = "1" OR
   - data-nova-proof attribute on html/body
   
   RUNTIME OUTPUT:
   - localStorage.nova_proof_last_run = JSON proof artifact
   - Emits 'nova:proof:update' event for overlay
   
   © 2025 Nova's Gems — Proof Pack v1.0
   ═══════════════════════════════════════════════════════════════ */

(() => {
  'use strict';
  if (window.NovaProof && window.NovaProof.__loaded) return;

  const NovaProof = (window.NovaProof = window.NovaProof || {});
  NovaProof.__loaded = true;
  NovaProof.__version = '1.0.0';

  const LS_KEY = 'nova_proof';
  const LS_LAST = 'nova_proof_last_run';

  const nowISO = () => new Date().toISOString();

  function isEnabled() {
    try {
      const ls = localStorage.getItem(LS_KEY);
      if (ls === '1' || ls === 'true') return true;
      if (document.documentElement.hasAttribute('data-nova-proof')) return true;
      if (document.body && document.body.getAttribute('data-nova-proof') === '1') return true;
    } catch (_) {}
    return false;
  }

  function safeJSONParse(str) {
    try { return JSON.parse(str); } catch (_) { return null; }
  }

  function detectPage() {
    const pageType = document.body?.dataset?.pageType || document.body?.getAttribute('data-page-type') || '';
    const template = document.body?.dataset?.template || document.body?.getAttribute('data-template') || '';
    return {
      url: location.href,
      pathname: location.pathname,
      pageType: pageType || 'unknown',
      template: template || 'unknown'
    };
  }

  function detectModules() {
    return {
      motion: window.NovaMotion?.__version || null,
      depthEngine: window.NovaDepthEngine?.__version || null,
      scrollNarrative: window.NovaScrollNarrative?.__version || null,
      vaultGallery: window.NovaVaultGallery?.__version || null,
      provenanceLab: window.NovaProvenanceLab?.__version || null,
      luxKit: window.NovaLuxKit?.__version || window.NovaInteractions?.__version || null,
      inspectionMode: window.NovaInspectionMode?.__version || null,
      cosmos: window.NovaCosmos?.__version || null
    };
  }

  function detectCapabilities() {
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pointerFine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
    return {
      reducedMotion: !!reducedMotion,
      pointerFine: !!pointerFine,
      intersectionObserver: 'IntersectionObserver' in window,
      performanceObserver: 'PerformanceObserver' in window
    };
  }

  function detectSections() {
    return {
      depthEngine: !!document.querySelector('[data-nova-depth-engine], .nova-depth-engine, canvas[data-nova-depth-canvas]'),
      chapterRail: !!document.querySelector('[data-nova-chapter-rail], .nova-chapter-rail, [data-chapter-rail]'),
      vaultGallery: !!document.querySelector('[data-nova-vault-gallery], .nova-vault-gallery'),
      provenanceLab: !!document.querySelector('[data-nova-provenance-lab], .nova-provenance-lab'),
      inspectionPanel: !!document.querySelector('[data-nova-inspection], .nova-inspection, dialog[data-nova-inspection-dialog]'),
      cosmosOverlay: !!document.querySelector('[data-nova-cosmos], .nova-cosmos-overlay')
    };
  }

  function checkGemData() {
    const el = document.querySelector('[data-nova-gem-data]');
    if (!el) return { ok: false, reason: 'missing [data-nova-gem-data]' };
    const json = safeJSONParse(el.textContent || '');
    if (!json) return { ok: false, reason: 'gem data JSON parse failed' };
    return { ok: true, data: json };
  }

  function checkJSONLD() {
    const scripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
    if (!scripts.length) return { ok: false, reason: 'missing ld+json script' };

    for (const s of scripts) {
      const json = safeJSONParse(s.textContent || '');
      if (!json) continue;

      const types = [];
      const pushType = (t) => { if (t) types.push(t); };

      if (Array.isArray(json)) json.forEach((x) => pushType(x && x['@type']));
      else pushType(json['@type']);

      const hasProduct = types.some((t) => (Array.isArray(t) ? t.includes('Product') : String(t).toLowerCase() === 'product'));
      if (hasProduct) return { ok: true };
    }
    return { ok: false, reason: 'ld+json present but no Product @type found' };
  }

  function runChecks() {
    const caps = detectCapabilities();
    const secs = detectSections();

    const gemData = checkGemData();
    const jsonld = checkJSONLD();

    const checks = {
      reduced_motion_check: caps.reducedMotion ? 'PASS' : 'NOT RUN (not reduced)',
      section_vault_check: secs.vaultGallery ? 'PASS' : 'FAIL',
      section_provenance_check: secs.provenanceLab ? 'PASS' : 'FAIL',
      gem_data_check: gemData.ok ? 'PASS' : `FAIL: ${gemData.reason}`,
      jsonld_product_check: jsonld.ok ? 'PASS' : `FAIL: ${jsonld.reason}`,
      focus_visible_check: 'NOT RUN (manual)',
      editor_lifecycle_check: 'NOT RUN (manual)',
      bfcache_check: 'NOT RUN (manual)'
    };

    return { caps, secs, gemData, jsonld, checks };
  }

  function storeArtifact(payload) {
    try { localStorage.setItem(LS_LAST, JSON.stringify(payload)); } catch (_) {}
  }

  function emitOverlay(payload) {
    const event = new CustomEvent('nova:proof:update', { detail: payload });
    document.dispatchEvent(event);
  }

  async function loadVitalsIfNeeded() {
    if (!isEnabled()) return null;
    if (window.NovaWebVitalsLite && window.NovaWebVitalsLite.get) return window.NovaWebVitalsLite;
    
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (window.NovaWebVitalsLite && window.NovaWebVitalsLite.get) {
          clearInterval(check);
          resolve(window.NovaWebVitalsLite);
        }
      }, 50);
      setTimeout(() => { clearInterval(check); resolve(null); }, 3000);
    });
  }

  async function tick() {
    if (!isEnabled()) return;

    const page = detectPage();
    const modules = detectModules();
    const base = runChecks();

    const vitalsLib = await loadVitalsIfNeeded();
    const vitals = vitalsLib?.get ? vitalsLib.get() : { lcp: null, cls: null, inp: null, fid: null };

    const payload = {
      timestamp: nowISO(),
      page,
      modules,
      capabilities: base.caps,
      sections: base.secs,
      checks: base.checks,
      vitals
    };

    storeArtifact(payload);
    emitOverlay(payload);
  }

  function init() {
    if (!isEnabled()) return;
    
    setTimeout(() => tick(), 1200);

    window.addEventListener('load', () => setTimeout(() => tick(), 500), { once: true });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) setTimeout(() => tick(), 300);
    });
  }

  document.addEventListener('shopify:section:load', () => init());
  document.addEventListener('shopify:section:unload', () => init());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  NovaProof.run = () => tick();
  NovaProof.isEnabled = isEnabled;
  NovaProof.getLastRun = () => {
    try {
      const v = localStorage.getItem(LS_LAST);
      return v ? JSON.parse(v) : null;
    } catch (_) {
      return null;
    }
  };
})();
